"use client";

import { useEffect, useId, useState } from "react";
import { useTheme } from "next-themes";

// 図は基本ニュートラル(card色 + border)で描き、色は意味があるところだけに使う。
// - :::main   強調したい主要ノード(primaryのborder + 薄いtint)
// - :::muted  補足的なノード・外部サービス
// - :::legacy 移行中・廃止予定(muted + 破線)
const palette = {
  light: {
    node: { fill: "#ffffff", stroke: "#d4d4d8", color: "#27272a" },
    main: { fill: "#fdf4ec", stroke: "#e67a2e", color: "#27272a" },
    muted: { fill: "#fafafa", stroke: "#d4d4d8", color: "#52525c" },
  },
  dark: {
    node: { fill: "#1c1c1f", stroke: "#3f3f46", color: "#e4e4e7" },
    main: { fill: "#2b2019", stroke: "#ff9a44", color: "#f4f4f5" },
    muted: { fill: "#151517", stroke: "#333338", color: "#a1a1aa" },
  },
} as const;

function classDefs(mode: "light" | "dark"): string {
  const p = palette[mode];
  return [
    `classDef main fill:${p.main.fill},stroke:${p.main.stroke},color:${p.main.color},stroke-width:1.5px`,
    `classDef muted fill:${p.muted.fill},stroke:${p.muted.stroke},color:${p.muted.color},stroke-width:1.5px`,
    `classDef legacy fill:${p.muted.fill},stroke:${p.muted.stroke},color:${p.muted.color},stroke-width:1.5px,stroke-dasharray:5 4`,
  ].join("\n");
}

function themeVariables(mode: "light" | "dark") {
  const dark = mode === "dark";
  const p = palette[mode];
  const line = dark ? "#71717a" : "#a1a1aa";
  const text = dark ? "#d4d4d8" : "#3f3f46";
  return {
    fontSize: "14px",
    // class無しのデフォルトノード
    primaryColor: p.node.fill,
    primaryBorderColor: p.node.stroke,
    primaryTextColor: p.node.color,
    lineColor: line,
    textColor: text,
    // subgraph
    clusterBkg: dark ? "#0f0f1180" : "#fafafa80",
    clusterBorder: p.node.stroke,
    // エッジラベルはページ背景に馴染ませる
    edgeLabelBackground: dark ? "#171717" : "#ffffff",
    // sequence diagram
    actorBkg: p.node.fill,
    actorBorder: p.node.stroke,
    actorTextColor: p.node.color,
    actorLineColor: line,
    signalColor: text,
    signalTextColor: text,
    labelBoxBkgColor: p.muted.fill,
    labelBoxBorderColor: p.node.stroke,
    labelTextColor: text,
    loopTextColor: text,
    noteBkgColor: p.main.fill,
    noteBorderColor: p.main.stroke,
    noteTextColor: p.node.color,
    activationBkgColor: p.muted.fill,
    activationBorderColor: line,
    sequenceNumberColor: dark ? "#0f0f11" : "#ffffff",
    // erDiagram: 行の縞々はデフォルトが白系のため、テーマ別に指定しないと
    // ダークモードで文字(textColor)と重なって読めなくなる
    attributeBackgroundColorOdd: p.node.fill,
    attributeBackgroundColorEven: p.muted.fill,
  };
}

// mermaid.renderへ渡すIDはdocument全体で一意である必要がある。
// useIdはページをまたぐと同じ値になり得るため(":r1:"など)、
// クライアント遷移時に前ページのSVGと衝突しないよう連番を足す。
let renderSeq = 0;

export function Mermaid({ chart }: { chart: string }) {
  const id = useId().replace(/[^a-zA-Z0-9-]/g, "");
  const { resolvedTheme } = useTheme();
  const [svg, setSvg] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const mode = resolvedTheme === "dark" ? "dark" : "light";

    async function render() {
      const { default: mermaid } = await import("mermaid");
      mermaid.initialize({
        startOnLoad: false,
        securityLevel: "loose",
        theme: "base",
        themeVariables: themeVariables(mode),
        fontFamily: "inherit",
        flowchart: {
          curve: "basis",
          nodeSpacing: 36,
          rankSpacing: 44,
          padding: 8,
        },
        sequence: {
          actorMargin: 48,
          mirrorActors: false,
        },
      });
      const source = chart.trimStart().startsWith("flowchart")
        ? `${chart}\n${classDefs(mode)}`
        : chart;
      const renderId = `mermaid-${id}-${++renderSeq}`;
      try {
        const { svg } = await mermaid.render(renderId, source);
        if (!cancelled) setSvg(svg);
      } finally {
        // renderが失敗すると一時要素がbodyに残り、ページ上へ重なって表示される
        document.getElementById(renderId)?.remove();
        document.getElementById(`d${renderId}`)?.remove();
      }
    }

    void render();
    return () => {
      cancelled = true;
    };
  }, [chart, id, resolvedTheme]);

  if (!svg) return null;

  return (
    <div
      className={
        chart.trimStart().startsWith("erDiagram")
          ? "my-6 overflow-x-auto [&_svg]:min-w-[64rem]"
          : "my-6 flex justify-center overflow-x-auto [&_svg]:max-w-full"
      }
      dangerouslySetInnerHTML={{ __html: svg }}
    />
  );
}
